<template>
  <div class="relative select-none" :class="props.rootClass || 'inline-block'" ref="containerRef" @click.stop>
    <!-- Trigger Button -->
    <button
      type="button"
      @click="toggleDropdown"
      :disabled="props.disabled"
      class="flex items-center justify-between gap-1.5 rounded-lg border text-xs font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      :class="[
        props.buttonClass || 'bg-white dark:bg-gray-700/90 hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200/90 dark:border-gray-600 text-gray-800 dark:text-gray-100 px-2.5 py-1.5 shadow-xs',
        isOpen ? 'ring-2 ring-indigo-500/30 border-indigo-500/50' : ''
      ]"
      :title="props.title || selectedLabel"
    >
      <div class="flex items-center gap-1.5 min-w-0 truncate">
        <!-- Optional Icon (e.g. Instrument emoji or custom icon) -->
        <span v-if="props.icon" class="text-sm shrink-0 leading-none select-none">{{ props.icon }}</span>
        <span class="truncate">{{ selectedLabel }}</span>
      </div>

      <!-- Single Custom Chevron Icon (rotates smoothly) -->
      <svg
        class="w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform duration-200"
        :class="isOpen ? 'rotate-180 text-indigo-600 dark:text-indigo-400' : ''"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95 -translate-y-1"
      enter-to-class="transform opacity-100 scale-100 translate-y-0"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100 translate-y-0"
      leave-to-class="transform opacity-0 scale-95 -translate-y-1"
    >
      <div
        v-if="isOpen"
        class="absolute z-[999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1 max-h-60 overflow-y-auto focus:outline-none min-w-[140px] text-xs"
        :class="[
          dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
          props.align === 'right' ? 'right-0' : 'left-0',
          props.menuClass || 'w-max'
        ]"
      >
        <!-- Grouped Options (if groups prop provided) -->
        <template v-if="props.groups && props.groups.length > 0">
          <div v-for="group in props.groups" :key="group.label" class="py-1 first:pt-0 last:pb-0">
            <div class="px-3 py-1 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-50/70 dark:bg-gray-900/30 select-none">
              {{ group.label }}
            </div>
            <button
              type="button"
              v-for="opt in group.options"
              :key="getOptionValue(opt)"
              @click="selectOption(getOptionValue(opt))"
              class="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors cursor-pointer"
              :class="[
                isSelected(getOptionValue(opt))
                  ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 font-bold'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              ]"
            >
              <span class="truncate">{{ getOptionLabel(opt) }}</span>
              <svg
                v-if="isSelected(getOptionValue(opt))"
                class="w-3.5 h-3.5 shrink-0 text-indigo-600 dark:text-indigo-400 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        </template>

        <!-- Flat Options -->
        <template v-else>
          <button
            type="button"
            v-for="opt in normalizedOptions"
            :key="opt.value"
            @click="selectOption(opt.value)"
            class="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors cursor-pointer"
            :class="[
              isSelected(opt.value)
                ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 font-bold'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            ]"
          >
            <span class="truncate">{{ opt.label }}</span>
            <svg
              v-if="isSelected(opt.value)"
              class="w-3.5 h-3.5 shrink-0 text-indigo-600 dark:text-indigo-400 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: ''
  },
  options: {
    type: Array,
    default: () => []
  },
  groups: {
    type: Array,
    default: null
  },
  icon: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Selecione...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  rootClass: {
    type: String,
    default: ''
  },
  align: {
    type: String,
    default: 'left' // 'left' | 'right'
  },
  buttonClass: {
    type: String,
    default: ''
  },
  menuClass: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const isOpen = ref(false);
const containerRef = ref(null);

const normalizedOptions = computed(() => {
  return props.options.map(opt => {
    if (typeof opt === 'object' && opt !== null) {
      return {
        value: opt.value !== undefined ? opt.value : opt.id,
        label: opt.label !== undefined ? opt.label : (opt.name || opt.value)
      };
    }
    return { value: opt, label: String(opt) };
  });
});

const getOptionValue = (opt) => {
  if (typeof opt === 'object' && opt !== null) {
    return opt.value !== undefined ? opt.value : opt.id;
  }
  return opt;
};

const getOptionLabel = (opt) => {
  if (typeof opt === 'object' && opt !== null) {
    return opt.label !== undefined ? opt.label : (opt.name || opt.value);
  }
  return String(opt);
};

const isSelected = (val) => {
  return String(val) === String(props.modelValue);
};

const selectedLabel = computed(() => {
  const currentVal = props.modelValue;
  if (props.groups && props.groups.length > 0) {
    for (const grp of props.groups) {
      const found = grp.options.find(opt => isSelected(getOptionValue(opt)));
      if (found) return getOptionLabel(found);
    }
  }
  const found = normalizedOptions.value.find(opt => isSelected(opt.value));
  return found ? found.label : (props.placeholder || String(currentVal ?? ''));
});

const dropUp = ref(false);

const toggleDropdown = () => {
  if (props.disabled) return;
  if (!isOpen.value && containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    dropUp.value = spaceBelow < 220 && rect.top > 220;
  }
  isOpen.value = !isOpen.value;
};

const selectOption = (val) => {
  emit('update:modelValue', val);
  emit('change', val);
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

const handleKeyDown = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('pointerdown', handleClickOutside);
  document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleClickOutside);
  document.removeEventListener('keydown', handleKeyDown);
});
</script>
